/**
 * Générateur — Reconnaître graphiquement une situation de proportionnalité
 * Attendu ID : 987 | Niveau : 5ème | Domaine : proportionnalite
 *
 * Types générés :
 *   - graphique_qcm_unique
 *   - graphique_qcm_multiple
 *   - graphique_tableau_vf
 *   - graphique_texte      : QCM verdict + justification (voir ci-dessous)
 *   - graphique_classer
 *   - graphique_relier
 *   - graphique_texte_trou
 *
 * TOUS les formats de cet attendu montrent maintenant de VRAIS nuages de points
 * (correction D5, bilan « À AJUSTER ») : « classer » et « relier » manipulaient
 * des DESCRIPTIONS qui énonçaient le critère de réponse (« droite passant par
 * (0 ; 0) », « dispersés »…), donc se réussissaient par mots-clés sans lire un
 * seul graphique ; « graphique_texte » demandait un oui/non en saisie libre,
 * soit 50 % au hasard. La clé `graphique_texte` est conservée (registre, quiz
 * enregistrés) bien que le format soit désormais un QCM.
 *
 * Règles pédagogiques (inspirées de Sésamath) :
 *   - Un nuage de points représente une proportionnalité SSI les points sont
 *     alignés sur une droite passant par l'origine (0 ; 0).
 *   - Piège majeur : droite ne passant pas par l'origine (aligné ≠ proportionnel).
 *   - Piège : courbe croissante prise pour proportionnelle.
 *   - Piège : augmentation régulière sans vérifier le passage par origine.
 *   - Les données discrètes sont des points isolés (pas une courbe continue).
 *
 * Mécanismes-clés testés :
 *   - Alignement des points sur une droite
 *   - Passage de la droite par l'origine (0 ; 0)
 *   - Distinction droite / courbe
 *   - Calcul du coefficient de proportionnalité (y/x constant)
 *
 * Variantes estimées : > 800
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeRelier, makeClasser, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 987;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Labels ───
const L_QCM = 'Identifier graphiquement une situation de proportionnalité';
const L_QCM_MULTI = 'Identifier les graphiques représentant une proportionnalité';
const L_VF = 'Vrai/Faux sur des graphiques et la proportionnalité';
const L_SAISIE = 'Justifier si un graphique représente une proportionnalité';
const L_CLASSER = 'Classer les graphiques selon leur nature';
const L_RELIER = 'Associer un graphique à sa nature';
const L_TROU = 'Compléter une justification sur un graphique';

// ─── Pools de contextes ───

const CONTEXTES = [
    { x: 'masse de tomates', y: 'prix', unitX: 'kg', unitY: '€' },
    { x: 'nombre de bonbons', y: 'prix', unitX: 'bonbons', unitY: '€' },
    { x: 'distance parcourue', y: 'temps', unitX: 'km', unitY: 'min' },
    { x: 'durée de location', y: 'prix', unitX: 'h', unitY: '€' },
    { x: 'nombre de cahiers', y: 'prix total', unitX: 'cahiers', unitY: '€' },
    { x: 'quantité d\'essence', y: 'prix', unitX: 'L', unitY: '€' },
    { x: 'surface de gazon', y: 'nombre de sacs', unitX: 'm²', unitY: 'sacs' },
    { x: 'nombre de stylos', y: 'prix', unitX: 'stylos', unitY: '€' },
    { x: 'kilomètres parcourus', y: 'coût du trajet', unitX: 'km', unitY: '€' },
];

// ─── Helpers de génération de points ───

/**
 * Génère un set de points proportionnels (y = k*x, avec point (0,0)).
 */
const genPointsProp = (k, count = 4) => {
    const points = [[0, 0]];
    const xValues = new Set([0]);
    
    while (points.length < count) {
        const x = randInt(1, 10);
        if (!xValues.has(x)) {
            xValues.add(x);
            points.push([x, k * x]);
        }
    }
    
    return points.sort((a, b) => a[0] - b[0]);
};

/**
 * Génère un set de points alignés mais ne passant pas par l'origine (y = k*x + b, b ≠ 0).
 */
const genPointsDroiteNonOrigine = (k, b, count = 4) => {
    const points = [];
    const xValues = new Set();
    
    while (points.length < count) {
        const x = randInt(1, 10);
        if (!xValues.has(x)) {
            xValues.add(x);
            points.push([x, k * x + b]);
        }
    }
    
    return points.sort((a, b) => a[0] - b[0]);
};

/**
 * Génère un set de points sur une courbe y = a·x².
 *
 * Abscisses CONSÉCUTIVES et y exact (aucun arrondi) : la courbure doit se voir
 * franchement. La variante y = a·√x, qui existait ici, produisait des nuages
 * quasi rectilignes — indiscernables à l'œil d'une « droite ne passant pas par
 * l'origine ». Tant que les formats décrivaient le graphique en toutes lettres
 * c'était sans conséquence ; maintenant que l'élève doit le LIRE, deux cibles
 * de l'exercice deviendraient également défendables pour le même nuage.
 */
const genPointsCourbe = (a = 2, count = 4) => {
    // x0 ∈ {1, 2} : avec x0 = 3, la portion de parabole vue est presque droite
    // (écart à la corde ≈ 8 % de la hauteur) → courbe/droite deviendraient
    // ambigus dans le relier. En partant plus près de l'origine, la courbure
    // reste franche (≥ 12 % de la hauteur).
    const x0 = randInt(1, 2);
    const points = [];
    for (let i = 0; i < count; i++) {
        const x = x0 + i;
        points.push([x, a * x * x]);
    }
    return points;
};

/**
 * Génère un set de points dispersés (pas d'alignement clair).
 */
const genPointsDisperse = (count = 4) => {
    // Abscisses toutes DISTINCTES (comme les autres générateurs de points) : deux
    // points de même x — une même surface donnant deux nombres de sacs différents —
    // sont irréalistes dans ces contextes et dupliquent une graduation d'axe.
    // Le nuage doit en outre être VISIBLEMENT non aligné : on rejette les tirages
    // dont les ordonnées sont croissantes, qui se lisent comme une droite montante.
    for (let essai = 0; essai < 60; essai++) {
        const points = [];
        const xValues = new Set();
        while (points.length < count) {
            const x = randInt(1, 10);
            const y = randInt(3, 25);
            if (!xValues.has(x) && Math.abs(y - 2 * x) > 3) {
                xValues.add(x);
                points.push([x, y]);
            }
        }
        points.sort((p, q) => p[0] - q[0]);
        const ys = points.map((p) => p[1]);
        const monotone = ys.every((y, i) => i === 0 || y >= ys[i - 1])
            || ys.every((y, i) => i === 0 || y <= ys[i - 1]);
        if (!monotone) return points;
    }
    // Filet : alternance forcée (ne devrait jamais servir)
    return Array.from({ length: count }, (_, i) => [i + 1, i % 2 === 0 ? 20 : 6]);
};

// ─── Helpers de description ───

const formatPoints = (points) => points.map(([x, y]) => `(${x} ; ${y})`).join(', ');

// ─── Rendu SVG d'un nuage de points — canal NORMATIF §12 FORMATS_TANOED ───
// Les figures passent par l'élément additionnel `image` (data-URI, rendu par
// ImageElementEleve : carte zoomable, fond « papier » garanti sur thème
// sombre). Le data-URI est CORRECT dans un élément d'énoncé — c'est dans
// `texte` qu'il serait strippé par DOMPurify, et le SVG inline dans `texte`
// s'affiche brut sur le fond sombre du quiz (illisible).
// Palette : encre #1e293b, secondaire #475569, données #1E3A8A (TanoEd).

const fmtNb = (n) => String(n).replace('.', ',');

/** Élément additionnel `image` (§12) portant un SVG en data-URI. */
const imgElement = (svg, maxWidth = 'compact') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
    } }],
});

const svgNuage = (points, w = 230, h = 180) => {
    const mL = 34, mB = 26, mT = 12, mR = 16;
    const xmax = Math.max(...points.map((p) => p[0]), 1);
    const ymax = Math.max(...points.map((p) => p[1]), 1);
    const X = (x) => mL + (x / xmax) * (w - mL - mR);
    const Y = (y) => (h - mB) - (y / ymax) * (h - mB - mT);
    const x0 = mL, y0 = h - mB;
    const dots = points.map(([x, y]) =>
        `<circle cx="${X(x).toFixed(1)}" cy="${Y(y).toFixed(1)}" r="4" fill="#1E3A8A"/>`).join('');
    const ticksX = points.filter(([x]) => x > 0).map(([x]) =>
        `<line x1="${X(x).toFixed(1)}" y1="${y0}" x2="${X(x).toFixed(1)}" y2="${y0 + 4}" stroke="#475569" stroke-width="1"/>`
        + `<text x="${X(x).toFixed(1)}" y="${y0 + 15}" text-anchor="middle" font-size="10" fill="#475569">${fmtNb(x)}</text>`).join('');
    // Quadrillage léger : sans repère, l'œil ne peut pas décider si l'alignement
    // passerait par (0 ; 0) — or c'est EXACTEMENT le critère à lire.
    let grille = '';
    for (let i = 1; i <= 5; i++) {
        const gx = mL + (i * (w - mL - mR)) / 6;
        grille += `<line x1="${gx.toFixed(1)}" y1="${mT}" x2="${gx.toFixed(1)}" y2="${y0}" stroke="#e2e8f0" stroke-width="1"/>`;
    }
    for (let j = 1; j <= 4; j++) {
        const gy = y0 - (j * (h - mB - mT)) / 5;
        grille += `<line x1="${mL}" y1="${gy.toFixed(1)}" x2="${w - mR}" y2="${gy.toFixed(1)}" stroke="#e2e8f0" stroke-width="1"/>`;
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial">`
        + grille
        + `<line x1="${x0}" y1="${y0}" x2="${w - 4}" y2="${y0}" stroke="#1e293b" stroke-width="1.5"/>`
        + `<line x1="${x0}" y1="${y0}" x2="${x0}" y2="6" stroke="#1e293b" stroke-width="1.5"/>`
        + `<polygon points="${w - 4},${y0} ${w - 12},${y0 - 4} ${w - 12},${y0 + 4}" fill="#1e293b"/>`
        + `<polygon points="${x0},6 ${x0 - 4},14 ${x0 + 4},14" fill="#1e293b"/>`
        + `<text x="${x0 - 5}" y="${y0 + 12}" text-anchor="end" font-size="10" fill="#475569">0</text>`
        + `<line x1="${x0 - 4}" y1="${Y(ymax).toFixed(1)}" x2="${x0}" y2="${Y(ymax).toFixed(1)}" stroke="#475569" stroke-width="1"/>`
        + `<text x="${x0 - 6}" y="${(Y(ymax) + 4).toFixed(1)}" text-anchor="end" font-size="10" fill="#475569">${fmtNb(ymax)}</text>`
        + ticksX + dots + `</svg>`;
};

/** Grille de nuages étiquetés (Graphique A, B, …) composée en UN SEUL SVG
    (sous-SVG imbriqués positionnés par x/y — un élément image par QCM). */
const svgGrille = (items, cols = 2, w = 230, h = 180) => {
    const bandeau = 22, gapX = 12, gapY = 14;
    const rows = Math.ceil(items.length / cols);
    const W = cols * (w + gapX) - gapX;
    const H = rows * (h + bandeau + gapY) - gapY;
    const cells = items.map(({ label, points }, i) => {
        const cx = (i % cols) * (w + gapX);
        const cy = Math.floor(i / cols) * (h + bandeau + gapY);
        return `<text x="${cx + w / 2}" y="${cy + 15}" text-anchor="middle" font-size="14" font-weight="bold" fill="#1e293b">${label}</text>`
            + `<svg x="${cx}" y="${cy + bandeau}" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`
            + svgNuage(points, w, h).replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '')
            + `</svg>`;
    }).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">${cells}</svg>`;
};

/** Points réels par type de nuage — un tirage par famille de situation. */
const POINTS_PAR_TYPE = {
    prop: () => genPointsProp(randomChoice([1, 1.5, 2, 2.5, 3]), 5),
    droiteNonOrigine: () => genPointsDroiteNonOrigine(randomChoice([1, 1.5, 2]), randomChoice([3, 4, 5, 6]), 5),
    courbe: () => genPointsCourbe(randomChoice([2, 3]), 5),
    disperse: () => genPointsDisperse(5),
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — Quel graphique représente une proportionnalité ?
// ═══════════════════════════════════════════════════════════

const FORMULATIONS_QCM = [
    () => `<p>Parmi ces graphiques, lequel représente une situation de proportionnalité ?</p>`,
    () => `<p>Quel graphique correspond à une relation de proportionnalité ?</p>`,
    () => `<p>Lequel de ces nuages de points traduit une proportionnalité ?</p>`,
    () => `<p>Quel graphique est associé à une situation de proportionnalité ?</p>`,
];

const genGraphiqueQcm = () => {
    const types = shuffle([
        { type: 'prop', isProp: true },
        { type: 'droiteNonOrigine', isProp: false },
        { type: 'courbe', isProp: false },
        { type: randomChoice(['disperse', 'courbe']), isProp: false },
    ]);
    
    const labels = ['Graphique A', 'Graphique B', 'Graphique C', 'Graphique D'];
    const items = types.map((t, i) => ({ label: labels[i], points: POINTS_PAR_TYPE[t.type]() }));

    const correctIndex = types.findIndex(t => t.isProp);
    const correct = labels[correctIndex];

    const texte = randomChoice(FORMULATIONS_QCM)();

    return makeQcm(labels, correct, texte,
        { ...opts(L_QCM), elements_additionnels: imgElement(svgGrille(items, 2), 'medium') });
};

// ═══════════════════════════════════════════════════════════
// 2. QCM MULTIPLE — Lesquels représentent une proportionnalité ?
// ═══════════════════════════════════════════════════════════

const genGraphiqueQcmMultiple = () => {
    const types = shuffle([
        { type: 'prop', isProp: true },
        { type: 'prop', isProp: true },
        { type: 'droiteNonOrigine', isProp: false },
        { type: 'courbe', isProp: false },
        { type: 'disperse', isProp: false },
        { type: randomChoice(['droiteNonOrigine', 'courbe']), isProp: false },
    ]);
    
    const labels = ['Graphique 1', 'Graphique 2', 'Graphique 3', 'Graphique 4', 'Graphique 5', 'Graphique 6'];
    const items = types.map((t, i) => ({ label: labels[i], points: POINTS_PAR_TYPE[t.type]() }));

    const correctLabels = types
        .map((t, i) => t.isProp ? labels[i] : null)
        .filter(l => l !== null);

    const texte = `<p>Parmi ces graphiques, lesquels représentent une situation de proportionnalité ?</p>`;

    return makeQcm(labels, correctLabels, texte,
        { ...opts(L_QCM_MULTI), elements_additionnels: imgElement(svgGrille(items, 3), 'large') },
        true);
};

// ═══════════════════════════════════════════════════════════
// 3. VRAI/FAUX — Affirmations sur des graphiques
// ═══════════════════════════════════════════════════════════

const genGraphiqueVF = () => {
    const allProps = [
        { text: `Un graphique avec des points alignés sur une droite passant par l'origine représente une proportionnalité.`, isVrai: true },
        { text: `Si les points d'un nuage forment une droite qui passe par (0 ; 0), alors la situation est proportionnelle.`, isVrai: true },
        { text: `Pour qu'un graphique traduise une proportionnalité, il faut que les points soient alignés ET que la droite passe par l'origine.`, isVrai: true },
        { text: `Si tous les rapports $\\frac{y}{x}$ sont égaux et la droite passe par l'origine, le graphique représente une proportionnalité.`, isVrai: true },
        { text: `Un graphique avec des points alignés représente toujours une proportionnalité.`, isVrai: false },
        { text: `Si un graphique montre une courbe croissante, alors la situation est proportionnelle.`, isVrai: false },
        { text: `Un nuage de points formant une droite qui ne passe pas par l'origine peut représenter une proportionnalité.`, isVrai: false },
        { text: `Des points dispersés sans alignement peuvent traduire une proportionnalité.`, isVrai: false },
        { text: `Si une grandeur augmente régulièrement, alors la situation est forcément proportionnelle.`, isVrai: false },
    ];
    
    const vrais = allProps.filter(p => p.isVrai);
    const faux = allProps.filter(p => !p.isVrai);
    
    const selected = [
        ...shuffle(vrais).slice(0, 3),
        ...shuffle(faux).slice(0, 3),
    ];
    
    const shuffled = shuffle(selected);
    const lignes = shuffled.map(p => p.text);
    const bonnes = shuffled.map(p => p.isVrai ? 0 : 1);
    
    return makeVraiFaux(lignes, bonnes, `<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`, opts(L_VF));
};

// ═══════════════════════════════════════════════════════════
// 4. QCM JUSTIFIÉ — Oui/non ET la raison (avec graphique visible)
// ═══════════════════════════════════════════════════════════

/**
 * Le verdict seul (« oui » / « non » en saisie libre) donnait 50 % de réussite
 * au hasard et n'apprenait rien sur ce que l'élève avait compris. On demande
 * maintenant le verdict ET sa justification.
 *
 * Règle de construction des distracteurs : chacun est FAUX sans ambiguïté —
 * soit il affirme sur le graphique quelque chose de matériellement inexact
 * (« les points ne sont pas alignés » sur un nuage aligné), soit il énonce un
 * critère de proportionnalité erroné (« il suffit que ça monte »). Aucun
 * distracteur n'est une phrase défendable : un élève qui raisonne juste ne
 * peut pas être compté faux.
 */
const JUSTIFICATIONS = {
    prop: {
        correct: 'Oui, car les points sont alignés et la droite passe par l\'origine.',
        faux: [
            'Oui, car il suffit que les points montent régulièrement.',
            'Non, car la droite ne passe pas par l\'origine.',
            'Non, car les points ne sont pas alignés.',
        ],
    },
    droiteNonOrigine: {
        correct: 'Non, car la droite ne passe pas par l\'origine.',
        faux: [
            'Oui, car il suffit que les points soient alignés.',
            'Oui, car les points sont alignés et la droite passe par l\'origine.',
            'Non, car les points ne sont pas alignés.',
        ],
    },
    courbe: {
        correct: 'Non, car les points ne sont pas alignés : ils forment une courbe.',
        faux: [
            'Oui, car il suffit que les valeurs augmentent.',
            'Oui, car les points sont alignés et la droite passe par l\'origine.',
            'Non, car il manque des valeurs entre les points.',
        ],
    },
    disperse: {
        correct: 'Non, car les points ne sont pas alignés.',
        faux: [
            'Oui, car il suffit que toutes les valeurs soient positives.',
            'Oui, car les points sont alignés et la droite passe par l\'origine.',
            'Non, car les points sont alignés mais la droite ne passe pas par l\'origine.',
        ],
    },
};

const genGraphiqueJustification = () => {
    // Les quatre familles sont ÉQUIPROBABLES : la proposition « alignés + passe
    // par l'origine » figure comme distracteur dans les trois autres jeux, et
    // c'est voulu (sinon sa seule présence trahirait la réponse). Encore
    // faut-il que la jouer systématiquement ne rapporte pas plus que le hasard.
    const type = randomChoice(['prop', 'droiteNonOrigine', 'courbe', 'disperse']);
    const context = randomChoice(CONTEXTES);
    const points = POINTS_PAR_TYPE[type]();
    const { correct, faux } = JUSTIFICATIONS[type];

    const texte = `<p>On a relevé des données sur ${context.x} et ${context.y}. Voici le graphique obtenu :</p>`
        + `<p>Ce graphique représente-t-il une situation de proportionnalité ?</p>`;

    return makeQcm([correct, ...faux], correct, texte, {
        ...opts(L_SAISIE),
        elements_additionnels: imgElement(svgNuage(points, 280, 210)),
    });
};

// ═══════════════════════════════════════════════════════════
// 5. CLASSER — Proportionnel / Non proportionnel
// ═══════════════════════════════════════════════════════════

/**
 * On classe de VRAIS nuages, pas leurs descriptions : les descriptions
 * contenaient littéralement le critère de réponse (« droite passant par
 * (0 ; 0) », « dispersés »…), l'élève triait par mots-clés sans jamais lire un
 * graphique. Les étiquettes à déplacer ne portent plus que la lettre du
 * graphique, et cette lettre est TIRÉE AU SORT : les proportionnels étaient
 * auparavant toujours A, B et C — réussite possible par simple position.
 */
const FORMULATIONS_CLASSER = [
    () => `<p>Six relevés ont été représentés graphiquement. Classe chaque graphique selon qu'il représente ou non une situation de proportionnalité.</p>`,
    () => `<p>Voici six nuages de points. Range chaque graphique dans la bonne colonne.</p>`,
    () => `<p>Chacun de ces six graphiques représente un relevé différent. Lesquels traduisent une proportionnalité ?</p>`,
    () => `<p>Observe les six graphiques, puis classe-les selon qu'ils correspondent ou non à une situation de proportionnalité.</p>`,
];

const genGraphiqueClasser = () => {
    const types = shuffle([
        'prop', 'prop', 'prop',
        'droiteNonOrigine',
        'courbe',
        randomChoice(['disperse', 'courbe', 'droiteNonOrigine']),
    ]);
    const labels = ['Graphique A', 'Graphique B', 'Graphique C',
        'Graphique D', 'Graphique E', 'Graphique F'];
    const items = types.map((type, i) => ({ label: labels[i], points: POINTS_PAR_TYPE[type]() }));

    const prop = labels.filter((_, i) => types[i] === 'prop');
    const nonProp = labels.filter((_, i) => types[i] !== 'prop');

    return makeClasser(
        [
            { name: 'Proportionnel', items: prop },
            { name: 'Non proportionnel', items: nonProp },
        ],
        randomChoice(FORMULATIONS_CLASSER)(),
        { ...opts(L_CLASSER), elements_additionnels: imgElement(svgGrille(items, 3), 'large') },
    );
};

// ═══════════════════════════════════════════════════════════
// 6. RELIER — Description → Nature (appariement 1:1 strict)
// ═══════════════════════════════════════════════════════════

/**
 * Même correction que « Classer » : la source ne décrit plus le graphique (la
 * cible nommait alors la raison déjà écrite dans la source — appariement par
 * mots-clés), elle le DÉSIGNE. Les quatre nuages sont affichés dans l'énoncé et
 * les quatre natures restent distinctes, comme l'exige un appariement 1:1.
 */
const FORMULATIONS_RELIER = [
    () => `<p>Relie chaque graphique à la nature de la situation qu'il représente.</p>`,
    () => `<p>Observe les quatre graphiques, puis relie chacun à sa nature.</p>`,
    () => `<p>À quelle nature correspond chacun de ces quatre nuages de points ?</p>`,
];

const genGraphiqueRelier = () => {
    const labels = ['Graphique A', 'Graphique B', 'Graphique C', 'Graphique D'];
    const types = shuffle(['prop', 'droiteNonOrigine', 'courbe', 'disperse']);

    const natureLabels = {
        prop: 'Proportionnel (droite passant par l\'origine)',
        droiteNonOrigine: 'Non proportionnel (droite ne passant pas par l\'origine)',
        courbe: 'Non proportionnel (courbe)',
        disperse: 'Non proportionnel (points dispersés)',
    };

    const items = types.map((type, i) => ({ label: labels[i], points: POINTS_PAR_TYPE[type]() }));
    const pairs = types.map((type, i) => ({ source: labels[i], target: natureLabels[type] }));

    return makeRelier(pairs, randomChoice(FORMULATIONS_RELIER)(), {
        ...opts(L_RELIER),
        elements_additionnels: imgElement(svgGrille(items, 2), 'medium'),
    });
};

// ═══════════════════════════════════════════════════════════
// 7. TEXTE À TROUS — Compléter une justification
// ═══════════════════════════════════════════════════════════

/**
 * Distracteurs PAR BRANCHE. Un jeu unique servait les trois justifications
 * possibles : la plupart des étiquettes n'étaient compatibles avec aucun trou
 * (« forment une {{dispersés}} »), l'élève posait la seule qui entrait
 * grammaticalement. Chaque étiquette doit ici être interchangeable avec la
 * bonne réponse dans la phrase, et rendre l'affirmation FAUSSE une fois posée
 * — jamais simplement bizarre, jamais accidentellement vraie.
 */
const genGraphiqueTrou = () => {
    const isProp = Math.random() < 0.5;
    const context = randomChoice(CONTEXTES);
    const texte = `<p>On a représenté graphiquement la relation entre ${context.x} et ${context.y} :</p>`
        + '<p>Complète la justification :</p>';

    if (isProp) {
        const k = randomChoice([2, 3, 4, 5]);
        const points = genPointsProp(k, 4);

        const phrase = 'Le graphique représente une situation de proportionnalité car les points '
            + 'sont {{alignés}} sur une droite et cette droite passe par {{l\'origine}}.';

        return makeTexteTrou(phrase, texte, {
            ...opts(L_TROU),
            elements_additionnels: imgElement(svgNuage(points, 280, 210)),
            mode: 'drag',
            // 2 adjectifs pour le 1er trou, 2 repères pour le 2nd : chaque trou a
            // de vrais concurrents. Aucun point cité n'est sur une droite y = kx.
            distracteurs: ['dispersés', 'espacés', 'le point (1 ; 0)', 'le point (0 ; 1)'],
        });
    }

    const raison = randomChoice(['droiteNonOrigine', 'courbe', 'disperse']);
    let points, phrase, distracteurs;

    if (raison === 'droiteNonOrigine') {
        const k = randomChoice([2, 3]);
        const b = randomChoice([2, 3, 4]);
        points = genPointsDroiteNonOrigine(k, b, 4);
        phrase = 'Le graphique ne représente pas une situation de proportionnalité car la droite '
            + 'ne passe pas par {{l\'origine}}.';
        // La droite coupe bien l'axe des ordonnées (en b > 0) : poser « l'axe des
        // ordonnées » rend la phrase fausse — c'est la confusion visée.
        distracteurs = ['l\'axe des ordonnées', 'le point (0 ; 1)', 'le point (1 ; 1)'];
    } else if (raison === 'courbe') {
        points = genPointsCourbe(randomChoice([1, 2]), 4);
        phrase = 'Le graphique ne représente pas une situation de proportionnalité car les points '
            + 'forment une {{courbe}} et non une droite.';
        distracteurs = ['demi-droite', 'droite verticale', 'ligne horizontale'];
    } else {
        points = genPointsDisperse(4);
        phrase = 'Le graphique ne représente pas une situation de proportionnalité car les points '
            + 'ne sont pas {{alignés}}.';
        // Les points SONT dispersés, espacés et distincts : chacune de ces
        // étiquettes rend la phrase fausse une fois posée.
        distracteurs = ['dispersés', 'espacés', 'distincts'];
    }

    // Le SVG passe par l'élément d'énoncé, JAMAIS inline dans `texte` : inline,
    // il est strippé par DOMPurify (ou s'affiche brut, illisible sur le fond
    // sombre du quiz) — l'élève complétait la justification d'un graphique
    // qu'il ne voyait pas.
    return makeTexteTrou(phrase, texte, {
        ...opts(L_TROU),
        elements_additionnels: imgElement(svgNuage(points, 280, 210)),
        mode: 'drag',
        distracteurs,
    });
};

// ─── Registre ───
const GENERATORS = {
    graphique_qcm_unique: genGraphiqueQcm,
    graphique_qcm_multiple: genGraphiqueQcmMultiple,
    graphique_tableau_vf: genGraphiqueVF,
    graphique_texte: genGraphiqueJustification,
    graphique_classer: genGraphiqueClasser,
    graphique_relier: genGraphiqueRelier,
    graphique_texte_trou: genGraphiqueTrou,
};

// ─── Catégories ───
export const CATEGORIES = [
    {
        id: 'graphique',
        label: 'Reconnaître graphiquement une situation de proportionnalité',
        formatsList: [
            { key: 'graphique_qcm_unique', label: 'QCM' },
            { key: 'graphique_qcm_multiple', label: 'QCM Multiple' },
            { key: 'graphique_tableau_vf', label: 'Vrai/Faux' },
            { key: 'graphique_texte', label: 'QCM justifié' },
            { key: 'graphique_classer', label: 'Classer' },
            { key: 'graphique_relier', label: 'Relier' },
            { key: 'graphique_texte_trou', label: 'Texte à trous' },
        ],
    },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
